import type { Board, Colour, GameState, Move, Player, PieceType } from './types';
import { material, other } from './board';
import { applyMove, isHeavy, legalMoves, pieceMoves, type TurnState } from './moves';

/**
 * A player policy: how to pick a move and how to answer a pending reverse.
 * `pick(n)` is a seeded integer in [0, n) so a whole game stays reproducible.
 */
export interface Policy {
  name: string;
  move(s: GameState, moves: Move[], pick: (n: number) => number): Move;
  /** Answer a reverse that would hurt this player: spend a veto or roll the save die. */
  reverse(s: GameState): 'veto' | 'save';
}

export const randomPolicy: Policy = {
  name: 'random',
  move: (_s, moves, pick) => moves[pick(moves.length)]!,
  reverse: (s) => (s.vetoes[s.hurt!] > 0 ? 'veto' : 'save'),
};

const VALUE: Record<PieceType, number> = { P: 1, N: 3, B: 3, R: 5, Q: 9, K: 100 };

/** Squares each colour attacks (pawns by their diagonals, others by their capture reach). */
function attackMaps(board: Board): Record<Colour, Uint8Array> {
  const maps = { l: new Uint8Array(64), d: new Uint8Array(64) };
  for (let i = 0; i < 64; i++) {
    const p = board[i];
    if (!p) continue;
    const map = maps[p.c];
    if (p.t === 'P') {
      const r = (i / 8) | 0;
      const f = i % 8;
      const rr = r + (p.c === 'l' ? -1 : 1);
      if (rr < 0 || rr > 7) continue;
      if (f > 0) map[rr * 8 + f - 1] = 1;
      if (f < 7) map[rr * 8 + f + 1] = 1;
    } else {
      for (const to of pieceMoves(board, i)) map[to] = 1;
    }
  }
  return maps;
}

/** Hand-written evaluation from `colour`'s point of view. Higher is better. */
export function evaluate(board: Board, colour: Colour, movesLeft: number): number {
  const foe = other(colour);
  const att = attackMaps(board);
  let myKing = -1;
  let foeKing = -1;
  for (let i = 0; i < 64; i++) {
    const p = board[i];
    if (p?.t === 'K') (p.c === colour ? (myKing = i) : (foeKing = i));
  }
  let score = 0;
  for (let i = 0; i < 64; i++) {
    const p = board[i];
    if (!p) continue;
    const mine = p.c === colour;
    const v = VALUE[p.t];
    score += mine ? v : -v;
    if (p.t === 'K') continue;
    // a piece the other side can take is worth less to its owner
    if (att[mine ? foe : colour]![i]) score += mine ? -0.4 * v : 0.25 * v;
    if (p.t === 'P') {
      const advance = p.c === 'l' ? 6 - ((i / 8) | 0) : ((i / 8) | 0) - 1;
      score += (mine ? 0.06 : -0.06) * advance;
    } else if (mine && foeKing >= 0) {
      const dist = Math.max(Math.abs((i % 8) - (foeKing % 8)), Math.abs(((i / 8) | 0) - ((foeKing / 8) | 0)));
      score -= 0.03 * dist;
    }
  }
  // being attacked at the end of the turn loses the king if the capturer can move
  if (myKing >= 0 && att[foe]![myKing]) score -= 60 * (movesLeft === 0 ? 1 : 0.35);
  if (foeKing >= 0 && att[colour]![foeKing]) score += 4;
  return score;
}

export const heuristicPolicy: Policy = {
  name: 'heuristic',
  move(s, moves, pick) {
    const colour = s.control[s.turn];
    let best = moves[0]!;
    let bestScore = -Infinity;
    for (const m of moves) {
      const board = s.board.slice();
      let captured: PieceType | undefined;
      if (m.kind === 'add') board[m.to] = { t: 'P', c: colour };
      else {
        captured = board[m.to]?.t;
        board[m.to] = board[m.from]!;
        board[m.from] = null;
      }
      // taking the king wins on the spot
      const score = captured === 'K' ? Infinity : evaluate(board, colour, s.movesLeft - 1) + pick(1000) / 100000;
      if (score > bestScore) {
        bestScore = score;
        best = m;
      }
    }
    return best;
  },
  reverse(s) {
    const hurt: Player = s.hurt!;
    const lead = Math.abs(material(s.board, s.control.A) - material(s.board, s.control.B));
    return s.vetoes[hurt] > 0 && lead >= 2 ? 'veto' : 'save';
  },
};

/* ---------- lookahead (expectimax over the d20) ---------- */

const move2 = (board: Board, m: Move, colour: Colour): { board: Board; captured: PieceType | undefined } => {
  const b = board.slice();
  if (m.kind === 'add') {
    b[m.to] = { t: 'P', c: colour };
    return { board: b, captured: undefined };
  }
  const captured = b[m.to]?.t;
  b[m.to] = b[m.from]!;
  b[m.from] = null;
  return { board: b, captured };
};

/**
 * Play out one turn of `n` moves greedily for `colour`. Returns the board, or
 * 'king' if the player takes the enemy king on the way.
 */
function greedyTurn(board: Board, colour: Colour, n: number, rules: GameState['rules']): Board | 'king' {
  return greedyTurnFrom({ board, movesLeft: n, heavyUsed: false, movedKings: { l: false, d: false }, rolled: n }, colour, rules);
}

const LOST = -1000;

/**
 * Value (from `me`'s point of view) of a position where `me` has just finished
 * a turn: the opponent's roll is a chance node over the d20 faces, and the
 * opponent answers each number face with a greedy turn.
 */
function expectedValue(board: Board, me: Colour, s: GameState): number {
  const opp = other(me);
  const { faces, saveFaces, saveSides } = s.rules;
  const total = faces['1'] + faces['2'] + faces['3'] + faces.skip + faces.reverse;
  const meP: Player = s.control.A === me ? 'A' : 'B';
  const oppP: Player = meP === 'A' ? 'B' : 'A';
  let value = 0;
  for (const n of [1, 2, 3] as const) {
    const w = faces[String(n) as '1' | '2' | '3'];
    if (!w) continue;
    const after = greedyTurn(board, opp, n, s.rules);
    value += (w / total) * (after === 'king' ? LOST : evaluate(after, me, 0));
  }
  value += (faces.skip / total) * evaluate(board, me, 1);
  if (faces.reverse) {
    const mine = material(board, me);
    const theirs = material(board, opp);
    const keep = evaluate(board, me, 1);
    const swapped = evaluate(board, opp, 1);
    let v = swapped;
    if (mine !== theirs) {
      const hurtP = mine > theirs ? meP : oppP;
      const lead = Math.abs(mine - theirs);
      const cancel = s.vetoes[hurtP] > 0 && lead >= 2 ? 1 : saveFaces / saveSides;
      v = cancel * keep + (1 - cancel) * swapped;
    }
    value += (faces.reverse / total) * v;
  }
  return value;
}

export interface LookaheadOptions {
  /** How many of the statically best first moves get the full lookahead. */
  topK?: number;
}

/**
 * A move-by-move search. For each promising move it finishes our own turn
 * greedily, then averages over the opponent's possible d20 rolls (with a
 * greedy reply to each), including the save die and veto on a reverse.
 */
export function lookaheadPolicy(opts: LookaheadOptions = {}): Policy {
  const topK = opts.topK ?? 8;
  return {
    name: 'lookahead',
    move(s, moves, pick) {
      const colour = s.control[s.turn];
      const scored: { m: Move; board: Board; score: number }[] = [];
      for (const m of moves) {
        const { board, captured } = move2(s.board, m, colour);
        if (captured === 'K') return m;
        scored.push({ m, board, score: evaluate(board, colour, s.movesLeft - 1) });
      }
      scored.sort((a, b) => b.score - a.score);
      let best = scored[0]!.m;
      let bestValue = -Infinity;
      for (const c of scored.slice(0, topK)) {
        let t: TurnState = { board: c.board, movesLeft: s.movesLeft - 1, heavyUsed: s.heavyUsed, movedKings: { ...s.movedKings }, rolled: s.rolled };
        const m0 = c.m;
        if (m0.kind === 'move') {
          const p = s.board[m0.from]!;
          if (p.t === 'K') t.movedKings[p.c] = true;
          if (isHeavy(p.t, s.rules.knightHeavy)) t.heavyUsed = true;
        }
        const rest = t.movesLeft > 0 ? greedyTurnFrom(t, colour, s.rules) : t.board;
        if (rest === 'king') return c.m;
        const v = expectedValue(rest, colour, s) + pick(1000) / 100000;
        if (v > bestValue) {
          bestValue = v;
          best = c.m;
        }
      }
      return best;
    },
    reverse: heuristicPolicy.reverse,
  };
}

/** Finish a turn already in progress greedily. */
function greedyTurnFrom(t: TurnState, colour: Colour, rules: GameState['rules']): Board | 'king' {
  const mr = { pawnRebuild: rules.pawnRebuild, knightHeavy: rules.knightHeavy };
  while (t.movesLeft > 0) {
    const moves = legalMoves(t, colour, mr);
    if (!moves.length) break;
    let best = moves[0]!;
    let bestScore = -Infinity;
    for (const m of moves) {
      const { board: b, captured } = move2(t.board, m, colour);
      if (captured === 'K') return 'king';
      const sc = evaluate(b, colour, t.movesLeft - 1);
      if (sc > bestScore) {
        bestScore = sc;
        best = m;
      }
    }
    t = applyMove(t, best, colour, rules.knightHeavy);
  }
  return t.board;
}
